import ReactGridLayout from 'react-grid-layout';
import { DynamicCardType, ICard, IDynamicCard } from 'src/app/pages/HomePage/Card/home-card.config';
import { IndustryRecognizedDataType } from 'src/app/services/interest/Industry/industry-service.type';
import { LocationRecognizedDataType } from 'src/app/services/interest/Location/location-service.type';
import { verifyLicense } from 'src/app/services/user/user.service';
import { finalUrlsConfig } from 'src/configs/url.config';
import { LanguagePreferenceEnum } from 'src/enums/language.enum';
import { LicenseEnum } from 'src/enums/user.enum';
import { EnterpriseRelationsResultType } from 'src/types/enterprise.type';

export const licenseRedirection = async (licenseId: LicenseEnum) => {
  try {
    const { data } = await verifyLicense({ licenseId }, { blockErrorHandler: true });
    let url = '';
    if (licenseId === LicenseEnum.ETECUBE) {
      url = finalUrlsConfig().etecubeURL;
    } else if (licenseId === LicenseEnum.HYMOTSG) {
      url = finalUrlsConfig().hymotsGURL;
    }
    url += `/Redirect?token=${data.token}&licenseId=${data.licenseId}&source=nginr`;
    window.open(url, '_self');
  } catch (error) {
    console.log('error', error);
  }
};

export const getStaticCardBg = (idx: number) => {
  const colors: string[] = ['#CFDBD5', '#E8EDDF', '#568259', '#263D42'];

  return colors[idx % colors.length];
};

export type DashboardState = {
  layout: ReactGridLayout.Layout[];
  // toolbox: string[];
};

// Take only default and suitable for current layout cards from Cards.ts file
export const filterCards = <T>(cards: ICard<T>[], permissions: string[]) => {
  return cards.filter((card) => {
    return permissionsValidator(card.permission, permissions);
  });
};

export const permissionsValidator = (cardPermissions: string | string[] | undefined, permissions: string[]) => {
  if (!cardPermissions) return true;
  if (typeof cardPermissions === 'string') {
    if (!permissions.includes(cardPermissions)) {
      return false;
    }
    return true;
  }
  if (typeof cardPermissions === 'object') {
    let result = false;
    cardPermissions.forEach((cardPermission) => {
      if (permissions.includes(cardPermission)) {
        result = true;
      }
    });
    return result;
  }
};

// LAYOUT OPTIONS ACCORDING TO CARD TYPE
export const getLayoutOptions = (type: DynamicCardType) => {
  let options: Partial<ReactGridLayout.Layout> = {
    isResizable: false,
    maxH: 1,
    maxW: 1,
  };
  switch (type) {
    case DynamicCardType.CHART:
      options = { isResizable: true, maxH: 2, maxW: 2 };
      break;
  }
  return options;
};

const normalizeLayoutItem = (item: ReactGridLayout.Layout): ReactGridLayout.Layout => {
  return {
    ...item,
    maxW: item.maxW ?? 2,
    maxH: item.maxH ?? 2,
    w: item.w ?? 1,
    h: item.h ?? 1,
  };
};

export const normalizeLayouts = (layouts: ReactGridLayout.Layouts): ReactGridLayout.Layouts => {
  const normalized: ReactGridLayout.Layouts = {};

  Object.entries(layouts).forEach(([breakpoint, layoutArray]) => {
    normalized[breakpoint] = layoutArray.map(normalizeLayoutItem);
  });

  return normalized;
};

/* 
This function takes cards and language as parameters
Through all cards it generates four columns card layout
If language is arabic is starts from last item in cards
*/
export const generateLayout = (
  items: IDynamicCard<any>[],
  lang: LanguagePreferenceEnum,
  cols: { [key: string]: number },
): { [key: string]: ReactGridLayout.Layout[] } => {
  const layouts: { [key: string]: ReactGridLayout.Layout[] } = {};

  const cardWidth = 1;
  const cardHeight = 1;

  Object.entries(cols).forEach(([breakpoint, colCount]) => {
    const cardsPerRow = Math.floor(colCount / cardWidth);
    const layout: ReactGridLayout.Layout[] = [];

    for (let index = 0; index < items.length; index += 1) {
      const card = lang === LanguagePreferenceEnum.AR ? items[items.length - 1 - index] : items[index];
      layout.push({
        i: card.id,
        x: (index % cardsPerRow) * cardWidth,
        y: Math.floor(index / cardsPerRow) * cardHeight,
        w: card.w || card.minW || cardWidth,
        h: card.h || card.minH || cardHeight,
        maxH: 2,
        maxW: 2,
        minH: card.minH,
        minW: card.minW,
        isResizable: card.isResizable ?? true,
        // ...getLayoutOptions(card.type),
      });
    }

    layouts[breakpoint] = layout;
  });
  return layouts;
};

export const getUniqueCountriesFromSuppliers = (
  suppliers: EnterpriseRelationsResultType['from']['rows'],
): LocationRecognizedDataType[] => {
  const result: LocationRecognizedDataType[] = [];

  suppliers.forEach((supplier) => {
    const { country } = supplier.fromInterest.enterpriseIdentified;
    const foundCountry = result.find((obj) => obj.id === country.id);
    if (!foundCountry) {
      result.push(country);
    }
  });

  return result;
};

export const getUniqueIndustriesFromSuppliers = (
  suppliers: EnterpriseRelationsResultType['from']['rows'],
): IndustryRecognizedDataType[] => {
  const result: IndustryRecognizedDataType[] = [];

  suppliers.forEach((supplier) => {
    const { industry } = supplier.fromInterest.enterpriseIdentified;
    const foundIndustry = result.find((obj) => obj.id === industry.id);
    if (!foundIndustry) {
      result.push(industry);
    }
  });

  return result;
};

export const getInitialBreakpoint = (width: number, breakpoints: Record<string, number>) => {
  return (
    Object.entries(breakpoints)
      .sort(([, a], [, b]) => b - a)
      .find(([, bpWidth]) => width >= bpWidth)?.[0] || 'lg'
  );
};
